import { LazySection } from "@/components/common/lazy-section";
import { NearbySUPsClient } from "@/components/nearby-sups/client";
import { LazyNearbyPlacesMap } from "@/components/project-or-sup/lazy-nearby-places-map";
import { AvailableSUPs } from "@/components/projects/available-sups";
import { NearbySupsSkeleton } from "@/components/projects/skeletons";
import { ProjectTitle } from "@/components/projects/title";
import { AdditionalFeatures } from "@/components/sup/additional-features";
import { GalleryLarge } from "@/components/sup/gallery/large";
import { GallerySmall } from "@/components/sup/gallery/small";
import { AmenitiesSection } from "@/components/sup/sections/amenities";
import { ProjectDetails } from "@/components/sup/sections/project-details";
import { SectionContent } from "@/components/sup/seller-unit/section-components";
import { NearbyProperty } from "@/types/sup";
import { ProjectPageData } from "@/utils/get-project-data";
import { toSentenceCase } from "@/utils/string/to-sentence-case";
import { PromotionalCardsSection } from "../promotional-cards/hooks";

interface ProjectSectionsProps {
	projectData: ProjectPageData;
	coordinates?: { lat: number; lng: number };
	nearByProjects: NearbyProperty[];
	id: string;
	categories: { name: string; code: string }[];
}

export function ProjectSections({ projectData, coordinates, nearByProjects, id, categories }: ProjectSectionsProps) {
	// Create supOne object for comparison feature
	const projectSupOne = {
		_id: projectData.id,
		bathrooms: [],
		bedrooms: [],
		builtUpAreas: [],
		cover: projectData.images?.[0]?.url || "",
		distance: 0,
		prices: [],
		type: "sp" as const,
		updatedAt: new Date(),
		locality: projectData.location,
		localityName: projectData.location,
		projectName: projectData.name,
		projectId: projectData.id,
		approvals: projectData.projectInfo.approvals ? projectData.projectInfo.approvals.split(", ") : [],
		documents: projectData.projectInfo.documents ? projectData.projectInfo.documents.split(", ") : [],
		bankApprovals: projectData.projectInfo.bankApprovals ? projectData.projectInfo.bankApprovals.split(", ") : [],
		amenities: projectData.amenities?.map(a => a.code) ?? [],
		totalBlocks: projectData.projectInfo.blocks,
		totalFloors: projectData.projectInfo.maxFloors,
		totalUnits: projectData.projectInfo.units,
		configurations: [],
		possessionStatuses: [projectData.ageStatus?.label === "Age" ? "Ready to move" : "UnderConstruction"],
	};

	return (
		<>
			<ProjectTitle name={projectData.name} location={projectData.location} projectId={id} supOne={projectSupOne} />

			{/* Mobile Gallery with skeleton */}
			<div className="@4xl:hidden mt-4 grid mb-3 h-[300px] -mx-2">
				<GallerySmall
					mediaList={projectData.images}
					className=""
					projectId={projectData.id}
					shouldPrioritizeCoverImage={true}
					sizes="(max-width: 480px) 100vw, (max-width: 768px) 80vw, 600px"
					categories={categories}
				/>
			</div>

			{/* Desktop Gallery with skeleton */}
			<div className="hidden @4xl:grid min-h-[500px] mt-4 px-4">
				<GalleryLarge
					images={projectData.images}
					categories={categories}
					projectId={projectData.id}
					shouldPrioritizeCoverImage={true}
					sizes="(max-width: 1024px) 80vw, 70vw"
				/>
			</div>

			{/* Promotional Card Slot 1 */}
			<PromotionalCardsSection pageType="project" className="my-6" slot={0} totalSlots={5} />

			{/* Available Units - Above the fold */}
			<SectionContent title="Available Units">
				<AvailableSUPs sups={projectData.availableSUPs} />
			</SectionContent>

			{/* Promotional Card Slot 2 */}
			<PromotionalCardsSection pageType="project" className="my-6" slot={1} totalSlots={5} />

			{/* Below-the-fold content with lazy loading */}
			{/* Details Table */}
			<SectionContent title="Project Details">
				<ProjectDetails
					supOne={{
						_id: projectData.id,
						bathrooms: [],
						bedrooms: [],
						builtUpAreas: [],
						cover: projectData.images?.[0]?.url || "",
						distance: 0,
						prices: [],
						type: "sp",
						updatedAt: new Date(),
						locality: projectData.location,
						approvals: projectData.projectInfo.approvals ? projectData.projectInfo.approvals.split(", ") : [],
						documents: projectData.projectInfo.documents ? projectData.projectInfo.documents.split(", ") : [],
						bankApprovals: projectData.projectInfo.bankApprovals
							? projectData.projectInfo.bankApprovals.split(", ")
							: [],
						amenities: (projectData.amenities || []).map(a => a.code),
						totalBlocks: projectData.projectInfo.blocks,
						totalFloors: projectData.projectInfo.maxFloors,
						totalUnits: projectData.projectInfo.units,
						configurations: [],
						possessionStatuses: [projectData.ageStatus?.label === "Age" ? "Ready to move" : "UnderConstruction"],
					}}
					isProjectPage
					waterSourceString={projectData.projectInfo.waterSource}
					ageString={projectData.ageStatus?.label + ": " + projectData.ageStatus?.value}
					projectData={{
						propertyType: projectData.propertyType?.map(toSentenceCase).join(", ") || undefined,
						specifications: projectData.specifications,
						area: projectData.area,
						roads: projectData.roads,
						reraId: projectData.rera?.id,
					}}
				/>
			</SectionContent>

			{/* Amenities */}
			<SectionContent title="Amenities">
				<AmenitiesSection amenities={(projectData.amenities || []).map(x => x.name)} />
			</SectionContent>

			{/* Promotional Card Slot 3 */}
			<PromotionalCardsSection pageType="project" className="my-6" slot={2} totalSlots={5} />

			{/* Additional Features */}
			{(projectData.additionalFeatures || []).length > 0 && (
				<SectionContent title="Additional Features">
					<AdditionalFeatures additionalFeatures={projectData.additionalFeatures || []} />
				</SectionContent>
			)}

			{/* Promotional Card Slot 4 */}
			<PromotionalCardsSection pageType="project" className="my-6" slot={3} totalSlots={5} />

			{/* Nearby Places Map */}
			<SectionContent title="Nearby Places">
				{coordinates && (
					<LazyNearbyPlacesMap center={coordinates} coordinates={[coordinates.lng, coordinates.lat]} className="mb-6" />
				)}
			</SectionContent>

			{/* Promotional Card Slot 5 */}
			<PromotionalCardsSection pageType="project" className="my-6" slot={4} totalSlots={5} />

			{/* Nearby Projects */}
			<SectionContent title="Nearby Projects">
				{coordinates && (
					<LazySection rootMargin="150px" threshold={0.1} fallback={<NearbySupsSkeleton />} ssrShowChildren>
						<NearbySUPsClient
							coordinates={coordinates}
							initialData={nearByProjects}
							distanceText={projectData.name}
							type="sp"
							excludeId={id}
							shouldPrioritizeCoverImage={false}
						/>
					</LazySection>
				)}
			</SectionContent>
		</>
	);
}
